/**
 * Automated Test Runner for Expense Parsing & Chat
 * Runs tests against Supabase edge functions with detailed reporting
 */

import { supabase } from '@/integrations/supabase/client';
import { expenseParsingTests, type ExpenseTestCase } from './expense-parsing-tests';
import { chatTests, type ChatTestCase } from './chat-tests';

interface TestResult {
  id: string;
  category: string;
  passed: boolean;
  input: string | string[];
  expected: any;
  actual: any;
  errors: string[];
  warnings: string[];
  executionTime: number;
  usedLLMFallback?: boolean;
}

interface TestSummary {
  totalTests: number;
  passed: number;
  failed: number;
  passRate: number;
  totalExecutionTime: number;
  avgExecutionTime: number;
  llmFallbackCount: number;
  categoryBreakdown: Record<string, { passed: number; total: number }>;
}

// ============ Expense Parsing Tests ============

function compareExpenseResults(expected: any, actual: any, testCase: ExpenseTestCase): string[] {
  const errors: string[] = [];
  
  // Amount comparison (with tolerance for floating point)
  if (expected.amount !== null && actual.amount !== null) {
    const diff = Math.abs(Number(expected.amount) - Number(actual.amount));
    if (diff > 0.01) {
      errors.push(`Amount mismatch: expected ${expected.amount}, got ${actual.amount}`);
    }
  } else if (expected.amount !== actual.amount) {
    errors.push(`Amount mismatch: expected ${expected.amount}, got ${actual.amount}`);
  }
  
  // Currency comparison
  if (expected.currency && actual.currency !== expected.currency) {
    errors.push(`Currency mismatch: expected ${expected.currency}, got ${actual.currency}`);
  }
  
  // Merchant comparison (case-insensitive partial match)
  if (expected.merchant) {
    const expMerchant = expected.merchant.toLowerCase();
    const actMerchant = (actual.merchant || '').toLowerCase();
    if (!actMerchant.includes(expMerchant) && !expMerchant.includes(actMerchant)) {
      errors.push(`Merchant mismatch: expected "${expected.merchant}", got "${actual.merchant}"`);
    }
  }
  
  // Category comparison
  if (expected.category && actual.category !== expected.category) {
    errors.push(`Category mismatch: expected ${expected.category}, got ${actual.category}`);
  }
  
  // Date comparison (handle various formats)
  if (expected.date) {
    const normalizeDate = (d: string) => {
      if (d === 'today') return new Date().toISOString().split('T')[0];
      if (d === 'yesterday') {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toISOString().split('T')[0];
      }
      return d;
    };
    
    const expectedDate = normalizeDate(expected.date);
    const actualDate = actual.date ? actual.date.split('T')[0] : null;
    
    if (actualDate !== expectedDate) {
      errors.push(`Date mismatch: expected ${expectedDate}, got ${actualDate}`);
    }
  }
  
  return errors;
}

async function runExpenseParsingTest(testCase: ExpenseTestCase): Promise<TestResult> {
  const startTime = Date.now();
  
  try {
    const { data, error } = await supabase.functions.invoke('parse-expense', {
      body: {
        text: testCase.input,
        context: {
          timezone: 'Europe/Amsterdam',
          preferredCurrency: 'EUR'
        }
      }
    });
    
    const executionTime = Date.now() - startTime;
    
    if (error) {
      return {
        id: testCase.id,
        category: testCase.category,
        passed: false,
        input: testCase.input,
        expected: testCase.expected,
        actual: null,
        errors: [`Edge function error: ${error.message}`],
        warnings: [],
        executionTime
      };
    }
    
    if (!data.success) {
      return {
        id: testCase.id,
        category: testCase.category,
        passed: false,
        input: testCase.input,
        expected: testCase.expected,
        actual: null,
        errors: [`Parse failed: ${data.error || 'unknown error'}`],
        warnings: [],
        executionTime
      };
    }
    
    const items = data.items || (data.data ? [data.data] : []);
    const actual = items[0];
    
    const errors = compareExpenseResults(testCase.expected, actual, testCase);
    const warnings: string[] = [];
    
    // Check if LLM fallback was used
    const usedLLMFallback = actual?.source === 'llm';
    if (usedLLMFallback && !testCase.allowLLMFallback) {
      warnings.push('LLM fallback used (expected rule-based parse)');
    }
    
    return {
      id: testCase.id,
      category: testCase.category,
      passed: errors.length === 0,
      input: testCase.input,
      expected: testCase.expected,
      actual,
      errors,
      warnings,
      executionTime,
      usedLLMFallback
    };
  } catch (err) {
    return {
      id: testCase.id,
      category: testCase.category,
      passed: false,
      input: testCase.input,
      expected: testCase.expected,
      actual: null,
      errors: [`Exception: ${err instanceof Error ? err.message : String(err)}`],
      warnings: [],
      executionTime: Date.now() - startTime
    };
  }
}

// ============ Chat Tests ============

async function runChatTest(testCase: ChatTestCase): Promise<TestResult> {
  const startTime = Date.now();
  const conversationHistory: any[] = [];
  
  try {
    let lastResponse: any = null;
    
    // Simulate multi-turn conversation
    for (let i = 0; i < testCase.userMessages.length; i++) {
      const userMessage = testCase.userMessages[i];
      conversationHistory.push({ role: 'user', content: userMessage });
      
      const { data, error } = await supabase.functions.invoke('parse-expense', {
        body: {
          text: userMessage,
          messages: conversationHistory,
          context: {
            timezone: 'Europe/Amsterdam',
            preferredCurrency: 'EUR',
            partial: lastResponse?.partial
          }
        }
      });
      
      if (error) {
        return {
          id: testCase.id,
          category: testCase.category,
          passed: false,
          input: testCase.userMessages,
          expected: testCase.expectedBehavior,
          actual: null,
          errors: [`Turn ${i + 1} error: ${error.message}`],
          warnings: [],
          executionTime: Date.now() - startTime
        };
      }
      
      lastResponse = data;
      
      if (data.success && !data.items[0]?.is_clarifying) {
        // Assistant parsed the expense successfully
        conversationHistory.push({
          role: 'assistant',
          content: `Got it: ${data.items[0].merchant} for ${data.items[0].amount} ${data.items[0].currency}`
        });
      } else if (data.items[0]?.is_clarifying) {
        // Assistant needs clarification
        conversationHistory.push({
          role: 'assistant',
          content: data.items[0].question || 'Can you provide more details?'
        });
      }
    }
    
    const executionTime = Date.now() - startTime;
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Validate behavior
    if (testCase.expectedBehavior.shouldParseExpense) {
      const finalItem = lastResponse?.items?.[0];
      
      if (!finalItem || finalItem.is_clarifying) {
        errors.push('Expected to parse expense but got clarification request');
      } else if (testCase.expectedBehavior.finalExpense) {
        // Compare final expense
        const expected = testCase.expectedBehavior.finalExpense;
        if (Math.abs(finalItem.amount - expected.amount) > 0.01) {
          errors.push(`Amount: expected ${expected.amount}, got ${finalItem.amount}`);
        }
        if (finalItem.currency !== expected.currency) {
          errors.push(`Currency: expected ${expected.currency}, got ${finalItem.currency}`);
        }
        if (finalItem.category !== expected.category) {
          errors.push(`Category: expected ${expected.category}, got ${finalItem.category}`);
        }
      }
    }
    
    if (testCase.expectedBehavior.shouldAskClarification) {
      const hadClarification = conversationHistory.some(msg => 
        msg.role === 'assistant' && (
          msg.content.includes('?') ||
          msg.content.toLowerCase().includes('how much') ||
          msg.content.toLowerCase().includes('what did you')
        )
      );
      
      if (!hadClarification) {
        errors.push('Expected clarification question but none found');
      }
    }
    
    return {
      id: testCase.id,
      category: testCase.category,
      passed: errors.length === 0,
      input: testCase.userMessages,
      expected: testCase.expectedBehavior,
      actual: lastResponse,
      errors,
      warnings,
      executionTime
    };
  } catch (err) {
    return {
      id: testCase.id,
      category: testCase.category,
      passed: false,
      input: testCase.userMessages,
      expected: testCase.expectedBehavior,
      actual: null,
      errors: [`Exception: ${err instanceof Error ? err.message : String(err)}`],
      warnings: [],
      executionTime: Date.now() - startTime
    };
  }
}

// ============ Test Runner ============

function generateSummary(results: TestResult[]): TestSummary {
  const categoryBreakdown: Record<string, { passed: number; total: number }> = {};
  
  results.forEach(result => {
    if (!categoryBreakdown[result.category]) {
      categoryBreakdown[result.category] = { passed: 0, total: 0 };
    }
    categoryBreakdown[result.category].total++;
    if (result.passed) {
      categoryBreakdown[result.category].passed++;
    }
  });
  
  const passed = results.filter(r => r.passed).length;
  const totalExecutionTime = results.reduce((sum, r) => sum + r.executionTime, 0);
  const llmFallbackCount = results.filter(r => r.usedLLMFallback).length;
  
  return {
    totalTests: results.length,
    passed,
    failed: results.length - passed,
    passRate: (passed / results.length) * 100,
    totalExecutionTime,
    avgExecutionTime: totalExecutionTime / results.length,
    llmFallbackCount,
    categoryBreakdown
  };
}

function printResults(results: TestResult[], summary: TestSummary, testType: string) {
  console.log(`\n${'='.repeat(80)}`);
  console.log(`📊 ${testType} TEST RESULTS`);
  console.log('='.repeat(80));
  
  console.log(`\n✅ Passed: ${summary.passed}/${summary.totalTests} (${summary.passRate.toFixed(1)}%)`);
  console.log(`❌ Failed: ${summary.failed}/${summary.totalTests}`);
  console.log(`⏱️  Total time: ${summary.totalExecutionTime}ms (avg: ${summary.avgExecutionTime.toFixed(0)}ms per test)`);
  if (testType === 'Expense Parsing') {
    console.log(`🤖 LLM fallbacks: ${summary.llmFallbackCount}/${summary.totalTests}`);
  }
  
  console.log('\n📂 Category Breakdown:');
  Object.entries(summary.categoryBreakdown).forEach(([category, stats]) => {
    const percentage = (stats.passed / stats.total) * 100;
    const icon = percentage === 100 ? '✅' : percentage >= 50 ? '⚠️' : '❌';
    console.log(`  ${icon} ${category}: ${stats.passed}/${stats.total} (${percentage.toFixed(0)}%)`);
  });
  
  const failures = results.filter(r => !r.passed);
  if (failures.length > 0) {
    console.log('\n❌ Failed Tests:');
    failures.forEach(result => {
      console.log(`\n  [${result.id}] ${result.category}`);
      console.log(`  Input: ${Array.isArray(result.input) ? result.input.join(' → ') : result.input}`);
      result.errors.forEach(err => console.log(`  ❌ ${err}`));
      if (result.warnings.length > 0) {
        result.warnings.forEach(warn => console.log(`  ⚠️  ${warn}`));
      }
    });
  }
  
  console.log('\n' + '='.repeat(80));
}

// ============ Main Test Function ============

export async function runAllTests(options: {
  expenseTests?: boolean;
  chatTests?: boolean;
  testIds?: string[];
  categories?: string[];
  stopOnFirstFailure?: boolean;
} = {}) {
  console.log('🧪 Starting Automated Test Suite...\n');
  
  const runExpense = options.expenseTests !== false;
  const runChat = options.chatTests !== false;
  
  // Run Expense Parsing Tests
  if (runExpense) {
    console.log('📝 Running Expense Parsing Tests...');
    const testsToRun = expenseParsingTests.filter(test => {
      if (options.testIds && !options.testIds.includes(test.id)) return false;
      if (options.categories && !options.categories.includes(test.category)) return false;
      return true;
    });
    
    const expenseResults: TestResult[] = [];
    for (const test of testsToRun) {
      const result = await runExpenseParsingTest(test);
      expenseResults.push(result);
      
      console.log(`  ${result.passed ? '✅' : '❌'} ${result.id}: ${result.passed ? 'PASS' : 'FAIL'} (${result.executionTime}ms)`);
      
      if (!result.passed && options.stopOnFirstFailure) {
        console.log('\n⛔ Stopping on first failure');
        break;
      }
    }
    
    const expenseSummary = generateSummary(expenseResults);
    printResults(expenseResults, expenseSummary, 'Expense Parsing');
  }
  
  // Run Chat Tests
  if (runChat) {
    console.log('\n💬 Running Chat Interaction Tests...');
    const testsToRun = chatTests.filter(test => {
      if (options.testIds && !options.testIds.includes(test.id)) return false;
      if (options.categories && !options.categories.includes(test.category)) return false;
      return true;
    });
    
    const chatResults: TestResult[] = [];
    for (const test of testsToRun) {
      const result = await runChatTest(test);
      chatResults.push(result);
      
      console.log(`  ${result.passed ? '✅' : '❌'} ${result.id}: ${result.passed ? 'PASS' : 'FAIL'} (${result.executionTime}ms)`);
      
      if (!result.passed && options.stopOnFirstFailure) {
        console.log('\n⛔ Stopping on first failure');
        break;
      }
    }
    
    const chatSummary = generateSummary(chatResults);
    printResults(chatResults, chatSummary, 'Chat Interaction');
  }
  
  console.log('\n✨ Test suite completed!\n');
}

// Make available globally for browser console
if (typeof window !== 'undefined') {
  (window as any).runAllTests = runAllTests;
  (window as any).runExpenseTests = () => runAllTests({ expenseTests: true, chatTests: false });
  (window as any).runChatTests = () => runAllTests({ expenseTests: false, chatTests: true });
  
  console.log('🧪 Test runner loaded! Available commands:');
  console.log('  runAllTests()           - Run all tests');
  console.log('  runExpenseTests()       - Run only expense parsing tests');
  console.log('  runChatTests()          - Run only chat interaction tests');
  console.log('  runAllTests({ testIds: ["test-01", "test-02"] })  - Run specific tests');
  console.log('  runAllTests({ categories: ["Simple English"] })    - Run specific categories');
}
